import { ThemeEvents, VariantUpdateEvent, StringingUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import {
  getDiscountedPrices,
  buildRegiosPdpInputs,
  applyRegiosSalePrices,
} from '@theme/regios-pricing';
import { resolveOverridePrices } from '@theme/custom-variant-map';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** Guards against stale Regios results after a fast variant switch. */
  #regiosToken = 0;
  /** True while a custom bat price override is shown, so a late Regios result can't overwrite it. */
  #priceOverrideActive = false;

  connectedCallback() {
    super.connectedCallback();

    // Apply Regios pricing for the initial variant (reveals the deferred price).
    this.#applyRegios(this.dataset.variantId);

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.stringingUpdate, this.#onStringingUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.stringingUpdate, this.#onStringingUpdate);
  }

  /** @param {StringingUpdateEvent} event */
  #onStringingUpdate = (event) => {
    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const wasPriceOverrideActive = this.#priceOverrideActive;
    this.#priceOverrideActive = event.detail.enabled && event.detail.variantId != null;
    if (event.detail.variantId) this.#setInstallmentsVariant(event.detail.variantId);
    // Back on the stock bat: re-apply any Regios sale the custom price had replaced.
    if (wasPriceOverrideActive && !this.#priceOverrideActive) {
      queueMicrotask(() => this.#applyRegios(this.dataset.variantId));
    }

    const { priceContainer } = this.refs;
    if (!priceContainer) {
      return;
    }

    // Restricted PDPs render "Not sold online" in the price slot.
    // Preserve that state and skip stringing price rewrites.
    if (priceContainer.querySelector('.price-item--not-sold-online')) {
      return;
    }

    const { saleCents, onSale, compareAtDisplayCents } = resolveOverridePrices(
      event.detail,
      Number(this.dataset.variantCompareAtPrice) || 0
    );
    const { moneyFormat, currency } = event.detail;
    const format = (/** @type {number} */ cents) => formatMoney(cents, moneyFormat, currency);

    // Custom bats: the markup was rendered for the stock variant, whose sale state can differ
    // from the custom variant's. Show the group that matches the variant being bought.
    if (event.detail.compareAtPrice != null) {
      const regularGroup = priceContainer.querySelector('.price__regular');
      const saleGroup = priceContainer.querySelector('.price__sale');
      if (regularGroup && saleGroup) {
        regularGroup.classList.toggle('price__hidden', onSale);
        saleGroup.classList.toggle('price__hidden', !onSale);
      }
    }

    if (onSale) {
      const salePrice = priceContainer.querySelector('.price-item--sale.price');
      if (salePrice && !salePrice.closest('.price__hidden')) {
        salePrice.textContent = format(saleCents);
      }

      priceContainer.querySelectorAll('.compare-at-price').forEach((compareElement) => {
        if (compareElement.closest('.price__hidden')) return;
        compareElement.textContent = format(compareAtDisplayCents);
      });
      return;
    }

    const regularGroup = priceContainer.querySelector('.price__regular:not(.price__hidden)');
    const regularPrice = regularGroup?.querySelector('.price');
    if (regularPrice) {
      regularPrice.textContent = format(saleCents);
      return;
    }

    priceContainer.querySelectorAll('.price').forEach((priceElement) => {
      if (priceElement.closest('.price__hidden')) return;
      if (priceElement.classList.contains('price-item--sale')) return;
      priceElement.textContent = format(saleCents);
    });
  };

  /**
   * @param {string|number|null|undefined} variantId
   */
  async #applyRegios(variantId) {
    const token = ++this.#regiosToken;
    const reveal = () => this.classList.remove('regios-pending');
    const priceContainer = this.querySelector('[ref="priceContainer"]');

    if (!priceContainer) {
      reveal();
      return;
    }

    const result = await getDiscountedPrices(
      buildRegiosPdpInputs(this.dataset.productId, variantId)
    );

    if (token !== this.#regiosToken) return;
    if (this.#priceOverrideActive) {
      reveal();
      return;
    }

    applyRegiosSalePrices(priceContainer, result);
    reveal();
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    if (newProductPrice instanceof HTMLElement && newProductPrice.dataset.variantPrice != null) {
      this.dataset.variantPrice = newProductPrice.dataset.variantPrice;
      this.dataset.variantCompareAtPrice = newProductPrice.dataset.variantCompareAtPrice ?? '0';
      this.dataset.variantId = newProductPrice.dataset.variantId ?? this.dataset.variantId ?? '';
    }

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    this.#setInstallmentsVariant(event.detail.resource?.id ?? '');

    queueMicrotask(() => this.#applyRegios(this.dataset.variantId));
  };

  /**
   * @param {string|number} variantId
   */
  #setInstallmentsVariant(variantId) {
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (!installmentsInput) return;

    installmentsInput.value = String(variantId);
    installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
